import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { getAdminSession } from '../adminSession';
import {
  fetchAdminAttendanceCalendar,
  fetchAttendanceStaff,
  getAttendanceErrorMessage,
  type AttendanceStaffMember,
} from '../staff/adminAttendanceApi';
import { getPayrollSettingsErrorMessage, savePayrollRecord } from './payrollSettingsApi';
import { numberKRW, formatHours } from './payrollFormat';
import './PayrollCalculator.scss';

type DeductionMode = 'none' | 'insurance' | 'freelance';
type HoursMode = 'manual' | 'attendance';
type PayType = 'hourly' | 'salary';

const WEEKS_PER_MONTH = 4.345;
const WEEKLY_ALLOWANCE_MIN_HOURS = 15;

// 정직원 추가근무: 통상시급 = 월 급여 ÷ 209시간, 연장근로 가산 1.5배, 1일 = 8시간
const SALARY_STANDARD_MONTHLY_HOURS = 209;
const OVERTIME_MULTIPLIER = 1.5;
const OVERTIME_HOURS_PER_DAY = 8;

function sanitizeDecimalInput(value: string) {
  const cleaned = value.replace(/[^\d.]/g, '');
  const [intPart, ...rest] = cleaned.split('.');
  return rest.length ? `${intPart}.${rest.join('').slice(0, 2)}` : intPart;
}

function parseDecimal(value: string) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

const DEFAULT_RATES = {
  pension: 4.75,
  health: 3.595,
  longTermCare: 6.57,
  employment: 0.9,
  freelance: 3.3,
};

function formatNumberInput(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '';
  return value.toLocaleString('ko-KR');
}

function parseNumberInput(value: string) {
  const digitsOnly = value.replace(/[^\d]/g, '');
  if (!digitsOnly) return 0;
  return Number(digitsOnly);
}

function parseRateInput(value: string) {
  const cleaned = value.replace(/[^\d.]/g, '');
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : 0;
}

function parseTimeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const normalized = String(value).trim().replace(/：/g, ':');
  const direct = normalized.match(/(?:^|T|\s)(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!direct) return null;
  return Number(direct[1]) * 60 + Number(direct[2]);
}

function calcWorkMinutes(clockIn: string | null | undefined, clockOut: string | null | undefined): number | null {
  const inMin = parseTimeToMinutes(clockIn);
  const outMin = parseTimeToMinutes(clockOut);
  if (inMin === null || outMin === null) return null;
  const diff = outMin - inMin;
  return diff > 0 ? diff : null;
}

function includesLunch(clockIn: string | null | undefined, clockOut: string | null | undefined): boolean {
  const inMin = parseTimeToMinutes(clockIn);
  const outMin = parseTimeToMinutes(clockOut);
  if (inMin === null || outMin === null) return false;
  return inMin < 13 * 60 && outMin > 12 * 60;
}

function previousYearMonth() {
  const d = new Date();
  const year = d.getFullYear();
  const prevMonth = d.getMonth(); // getMonth() is 0-indexed for the current month, i.e. already "current month - 1"
  if (prevMonth === 0) return { year: year - 1, month: 12 };
  return { year, month: prevMonth };
}

function formatHoursInput(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '';
  return (Math.round(value * 10) / 10).toLocaleString('ko-KR', { maximumFractionDigits: 1 });
}

export default function PayrollCalculator({ onShowRecordsList }: { onShowRecordsList?: () => void }) {
  const session = getAdminSession();

  const [payType, setPayType] = useState<PayType>('hourly');
  const [monthlySalary, setMonthlySalary] = useState(0);
  const [overtimeDaysInput, setOvertimeDaysInput] = useState('');
  const [overtimeHoursInput, setOvertimeHoursInput] = useState('');
  const overtimeDays = parseDecimal(overtimeDaysInput);
  const overtimeHours = parseDecimal(overtimeHoursInput);

  const [hoursMode, setHoursMode] = useState<HoursMode>('attendance');
  const [hourlyWage, setHourlyWage] = useState(0);
  const [dailyHours, setDailyHours] = useState(0);
  const [weeklyDays, setWeeklyDays] = useState(0);

  const [staffList, setStaffList] = useState<AttendanceStaffMember[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<number | null>(null);
  const [viewYear, setViewYear] = useState(() => previousYearMonth().year);
  const [viewMonth, setViewMonth] = useState(() => previousYearMonth().month);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceError, setAttendanceError] = useState<string | null>(null);
  const [fetchedSummary, setFetchedSummary] = useState<{ workDays: number; totalHours: number } | null>(null);
  const [totalHoursInput, setTotalHoursInput] = useState(0);
  const [employeeName, setEmployeeName] = useState('');

  const payslipRef = useRef<HTMLDivElement>(null);
  const [capturingPayslip, setCapturingPayslip] = useState(false);

  const [deductionMode, setDeductionMode] = useState<DeductionMode>('insurance');
  const [pensionRate, setPensionRate] = useState(DEFAULT_RATES.pension);
  const [healthRate, setHealthRate] = useState(DEFAULT_RATES.health);
  const [longTermCareRate, setLongTermCareRate] = useState(DEFAULT_RATES.longTermCare);
  const [employmentRate, setEmploymentRate] = useState(DEFAULT_RATES.employment);
  const [freelanceRate, setFreelanceRate] = useState(DEFAULT_RATES.freelance);
  const [incomeTax, setIncomeTax] = useState(0);
  const [etcDeduction, setEtcDeduction] = useState(0);

  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (payType === 'salary' && deductionMode !== 'insurance') {
      setDeductionMode('insurance');
    }
  }, [payType, deductionMode]);

  useEffect(() => {
    if (!session?.id) return;
    fetchAttendanceStaff(session.id, true)
      .then((list) => setStaffList(list))
      .catch(() => setStaffList([]));
  }, [session?.id]);

  // 시급급여 탭은 구분 '알바', 정직원급여 탭은 구분 '정직원'인 직원만
  const payStaffList = useMemo(() => {
    const employmentType = payType === 'hourly' ? '알바' : '정직원';
    return staffList.filter((member) => member.employmentType === employmentType);
  }, [staffList, payType]);

  useEffect(() => {
    setSelectedStaffId((prev) =>
      prev && payStaffList.some((m) => m.id === prev) ? prev : payStaffList[0]?.id ?? null,
    );
    setFetchedSummary(null);
  }, [payStaffList]);

  const selectedStaff = payStaffList.find((member) => member.id === selectedStaffId) ?? null;

  useEffect(() => {
    if ((payType === 'salary' || hoursMode === 'attendance') && selectedStaff) {
      setEmployeeName(selectedStaff.name);
    }
  }, [payType, hoursMode, selectedStaff]);

  const handleFetchAttendance = useCallback(async () => {
    if (!session?.id || !selectedStaffId) return;
    setAttendanceLoading(true);
    setAttendanceError(null);
    try {
      const data = await fetchAdminAttendanceCalendar(session.id, viewYear, viewMonth, selectedStaffId);
      let totalMinutes = 0;
      let lunchDays = 0;
      let workDays = 0;
      data.items
        .filter((r) => Number(r.userId) === Number(selectedStaffId))
        .forEach((r) => {
          const minutes = calcWorkMinutes(r.clockIn, r.clockOut);
          if (minutes !== null) {
            totalMinutes += minutes;
            workDays += 1;
            if (includesLunch(r.clockIn, r.clockOut)) lunchDays += 1;
          }
        });
      const netMinutes = Math.max(0, totalMinutes - lunchDays * 60);
      const totalHours = netMinutes / 60;
      setFetchedSummary({ workDays, totalHours });
      setTotalHoursInput(totalHours);
    } catch (err) {
      setAttendanceError(getAttendanceErrorMessage(err, '출퇴근 데이터를 불러오지 못했습니다.'));
      setFetchedSummary(null);
    } finally {
      setAttendanceLoading(false);
    }
  }, [session?.id, selectedStaffId, viewYear, viewMonth]);

  const handleCapturePayslip = useCallback(async () => {
    if (!payslipRef.current) return;
    setCapturingPayslip(true);
    try {
      const canvas = await html2canvas(payslipRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const safeName = (employeeName.trim() || '직원').replace(/\s+/g, '_');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `급여명세서_${safeName}_${viewYear}년${viewMonth}월.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      alert('스크린샷 저장에 실패했습니다.');
    } finally {
      setCapturingPayslip(false);
    }
  }, [employeeName, viewYear, viewMonth]);

  const goPrevMonth = () => {
    setFetchedSummary(null);
    if (viewMonth === 1) {
      setViewYear((y) => y - 1);
      setViewMonth(12);
      return;
    }
    setViewMonth((m) => m - 1);
  };

  const goNextMonth = () => {
    setFetchedSummary(null);
    if (viewMonth === 12) {
      setViewYear((y) => y + 1);
      setViewMonth(1);
      return;
    }
    setViewMonth((m) => m + 1);
  };

  useEffect(() => {
    if (hoursMode !== 'manual') return;
    setTotalHoursInput(dailyHours * weeklyDays * WEEKS_PER_MONTH);
  }, [hoursMode, dailyHours, weeklyDays]);

  const sourceMonthlyHours = totalHoursInput;

  const result = useMemo(() => {
    const weeklyHours = payType === 'hourly' ? sourceMonthlyHours / WEEKS_PER_MONTH : 0;
    const isAllowanceEligible = payType === 'hourly' && weeklyHours >= WEEKLY_ALLOWANCE_MIN_HOURS;
    const allowanceHours = isAllowanceEligible ? Math.min(8, (weeklyHours / 40) * 8) : 0;

    const monthlyWorkHours = payType === 'hourly' ? sourceMonthlyHours : 0;
    const monthlyAllowanceHours = allowanceHours * WEEKS_PER_MONTH;
    const monthlyTotalHours = monthlyWorkHours + monthlyAllowanceHours;

    const basePayMonthly = payType === 'hourly' ? monthlyWorkHours * hourlyWage : monthlySalary;
    const allowancePayMonthly = payType === 'hourly' ? monthlyAllowanceHours * hourlyWage : 0;

    const overtimeTotalHours =
      payType === 'salary' ? overtimeDays * OVERTIME_HOURS_PER_DAY + overtimeHours : 0;
    const ordinaryHourlyWage = payType === 'salary' ? monthlySalary / SALARY_STANDARD_MONTHLY_HOURS : 0;
    const overtimePay = Math.round(overtimeTotalHours * ordinaryHourlyWage * OVERTIME_MULTIPLIER);

    const grossPay = basePayMonthly + allowancePayMonthly + overtimePay;

    let pensionAmount = 0;
    let healthAmount = 0;
    let longTermCareAmount = 0;
    let employmentAmount = 0;
    let freelanceAmount = 0;

    if (deductionMode === 'insurance') {
      pensionAmount = (grossPay * pensionRate) / 100;
      healthAmount = (grossPay * healthRate) / 100;
      longTermCareAmount = (healthAmount * longTermCareRate) / 100;
      employmentAmount = (grossPay * employmentRate) / 100;
    } else if (deductionMode === 'freelance') {
      freelanceAmount = (grossPay * freelanceRate) / 100;
    }

    const manualDeductions = deductionMode === 'insurance' ? incomeTax + etcDeduction : etcDeduction;
    const totalDeductions =
      pensionAmount + healthAmount + longTermCareAmount + employmentAmount + freelanceAmount + manualDeductions;
    const netPay = grossPay - totalDeductions;

    return {
      weeklyHours,
      isAllowanceEligible,
      allowanceHours,
      monthlyWorkHours,
      monthlyAllowanceHours,
      monthlyTotalHours,
      basePayMonthly,
      allowancePayMonthly,
      overtimeTotalHours,
      ordinaryHourlyWage,
      overtimePay,
      grossPay,
      pensionAmount,
      healthAmount,
      longTermCareAmount,
      employmentAmount,
      freelanceAmount,
      manualDeductions,
      totalDeductions,
      netPay,
    };
  }, [
    payType,
    sourceMonthlyHours,
    hourlyWage,
    monthlySalary,
    overtimeDays,
    overtimeHours,
    deductionMode,
    pensionRate,
    healthRate,
    longTermCareRate,
    employmentRate,
    freelanceRate,
    incomeTax,
    etcDeduction,
  ]);

  const handleSave = useCallback(async () => {
    if (!session?.id) return;
    const trimmedName = employeeName.trim();
    if (!trimmedName) {
      setSettingsMessage({ type: 'error', text: '직원 이름을 입력해주세요.' });
      return;
    }
    if (payType === 'salary' && monthlySalary <= 0) {
      setSettingsMessage({ type: 'error', text: '월 급여를 입력해주세요.' });
      return;
    }

    setSavingSettings(true);
    setSettingsMessage(null);
    try {
      await savePayrollRecord(session.id, {
        staffId: payType === 'salary' || (payType === 'hourly' && hoursMode === 'attendance') ? selectedStaffId : null,
        employeeName: trimmedName,
        department: selectedStaff?.department ?? null,
        position: selectedStaff?.position ?? null,
        year: viewYear,
        month: viewMonth,
        payType,
        hoursMode,
        deductionMode,
        hourlyWage: payType === 'hourly' ? hourlyWage : 0,
        monthlySalary: payType === 'salary' ? monthlySalary : null,
        dailyHours: payType === 'hourly' && hoursMode === 'manual' ? dailyHours : null,
        weeklyDays: payType === 'hourly' && hoursMode === 'manual' ? weeklyDays : null,
        totalHoursInput: payType === 'hourly' ? totalHoursInput : 0,
        workDays: payType === 'hourly' ? fetchedSummary?.workDays ?? null : null,
        overtimeDays: payType === 'salary' ? overtimeDays : 0,
        overtimeHours: payType === 'salary' ? overtimeHours : 0,
        pensionRate,
        healthRate,
        longTermCareRate,
        employmentRate,
        freelanceRate,
        incomeTax,
        etcDeduction,
        result: {
          weeklyHours: result.weeklyHours,
          allowanceHours: result.allowanceHours,
          monthlyWorkHours: result.monthlyWorkHours,
          monthlyAllowanceHours: result.monthlyAllowanceHours,
          monthlyTotalHours: result.monthlyTotalHours,
          basePay: result.basePayMonthly,
          allowancePay: result.allowancePayMonthly,
          overtimePay: result.overtimePay,
          grossPay: result.grossPay,
          pensionAmount: result.pensionAmount,
          healthAmount: result.healthAmount,
          longTermCareAmount: result.longTermCareAmount,
          employmentAmount: result.employmentAmount,
          freelanceAmount: result.freelanceAmount,
          totalDeductions: result.totalDeductions,
          netPay: result.netPay,
        },
      });
      setSettingsMessage({ type: 'success', text: '급여명세서가 저장되었습니다.' });
    } catch (err) {
      setSettingsMessage({
        type: 'error',
        text: getPayrollSettingsErrorMessage(err, '급여명세서 저장에 실패했습니다.'),
      });
    } finally {
      setSavingSettings(false);
    }
  }, [
    session?.id,
    employeeName,
    payType,
    monthlySalary,
    hoursMode,
    selectedStaffId,
    selectedStaff,
    viewYear,
    viewMonth,
    deductionMode,
    hourlyWage,
    dailyHours,
    weeklyDays,
    totalHoursInput,
    fetchedSummary,
    overtimeDays,
    overtimeHours,
    pensionRate,
    healthRate,
    longTermCareRate,
    employmentRate,
    freelanceRate,
    incomeTax,
    etcDeduction,
    result,
  ]);

  return (
    <div className="payroll-calculator">
      <div className="payroll-calculator__pay-type-tabs">
        <button
          type="button"
          className={`payroll-calculator__pay-type-tab${payType === 'hourly' ? ' is-active' : ''}`}
          onClick={() => setPayType('hourly')}
        >
          시급급여
        </button>
        <button
          type="button"
          className={`payroll-calculator__pay-type-tab${payType === 'salary' ? ' is-active' : ''}`}
          onClick={() => setPayType('salary')}
        >
          정직원급여
        </button>
      </div>

      <p className="payroll-calculator__hint">
        {payType === 'hourly'
          ? `시급과 근무시간을 입력하면 주휴수당을 포함한 예상 월급을 자동으로 계산합니다. (월 평균 ${WEEKS_PER_MONTH}주 기준)`
          : '월 급여를 입력하면 공제액을 반영한 실수령액을 계산합니다.'}
      </p>

      <div className="payroll-calculator__layout">
      <div className="payroll-calculator__left">
      <div className="payroll-calculator__card">
        <h3 className="payroll-calculator__card-title">근무 정보</h3>

        {payType === 'hourly' && (
        <>
        <div className="payroll-calculator__tabs">
          <button
            type="button"
            className={`payroll-calculator__tab${hoursMode === 'attendance' ? ' is-active' : ''}`}
            onClick={() => setHoursMode('attendance')}
          >
            출퇴근 현황에서 불러오기
          </button>
          <button
            type="button"
            className={`payroll-calculator__tab${hoursMode === 'manual' ? ' is-active' : ''}`}
            onClick={() => setHoursMode('manual')}
          >
            직접 입력
          </button>
        </div>

        {hoursMode === 'attendance' && (
          <div className="payroll-calculator__attendance-fetch">
            <label className="payroll-calculator__field">
              <span>직원 선택</span>
              <select
                value={selectedStaffId ?? ''}
                onChange={(e) => {
                  setSelectedStaffId(e.target.value ? Number(e.target.value) : null);
                  setFetchedSummary(null);
                }}
              >
                {payStaffList.length === 0 && (
                  <option value="">구분이 '알바'인 직원이 없습니다</option>
                )}
                {payStaffList.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                    {member.role === 'admin' ? ' (대표)' : ''}
                    {member.department || member.position
                      ? ` · ${[member.department, member.position].filter(Boolean).join(' ')}`
                      : ''}
                  </option>
                ))}
              </select>
            </label>

            <div className="payroll-calculator__month-nav">
              <button type="button" onClick={goPrevMonth}>‹</button>
              <span>{viewYear}년 {viewMonth}월</span>
              <button type="button" onClick={goNextMonth}>›</button>
            </div>

            <button
              type="button"
              className="payroll-calculator__fetch-btn"
              disabled={!selectedStaffId || attendanceLoading}
              onClick={() => void handleFetchAttendance()}
            >
              {attendanceLoading ? '불러오는 중…' : '출퇴근 현황 불러오기'}
            </button>

            {attendanceError && <p className="payroll-calculator__error">{attendanceError}</p>}

            {fetchedSummary && (
              <div className="payroll-calculator__attendance-summary">
                <div className="payroll-calculator__attendance-stat">
                  <span className="payroll-calculator__attendance-stat-label">실제 출근일수</span>
                  <strong className="payroll-calculator__attendance-stat-value">
                    {fetchedSummary.workDays}<small>일</small>
                  </strong>
                </div>
                <div className="payroll-calculator__attendance-stat">
                  <span className="payroll-calculator__attendance-stat-label">순 근무시간</span>
                  <strong className="payroll-calculator__attendance-stat-value">
                    {formatHours(fetchedSummary.totalHours)}
                  </strong>
                </div>
                <p className="payroll-calculator__attendance-summary-hint">
                  {viewYear}년 {viewMonth}월 · 점심시간 제외 · 출퇴근 현황 데이터 기준
                </p>
              </div>
            )}
          </div>
        )}

        <div className="payroll-calculator__field-grid">
          <label className="payroll-calculator__field">
            <span>시급 (원)</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="예: 10,030"
              value={formatNumberInput(hourlyWage)}
              onChange={(e) => setHourlyWage(parseNumberInput(e.target.value))}
            />
          </label>

          <label className="payroll-calculator__field">
            <span>총 근무시간 (시간)</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="예: 173.8"
              value={formatHoursInput(totalHoursInput)}
              onChange={(e) => setTotalHoursInput(parseRateInput(e.target.value))}
            />
          </label>

          {hoursMode === 'manual' && (
            <>
              <label className="payroll-calculator__field">
                <span>하루 근무시간 (시간)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="예: 8"
                  value={formatNumberInput(dailyHours)}
                  onChange={(e) => setDailyHours(parseNumberInput(e.target.value))}
                />
              </label>
              <label className="payroll-calculator__field">
                <span>주 근무일수 (일)</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="예: 5"
                  value={formatNumberInput(weeklyDays)}
                  onChange={(e) => setWeeklyDays(parseNumberInput(e.target.value))}
                />
              </label>
            </>
          )}
        </div>

        <p className="payroll-calculator__note">
          주 근무시간 {result.weeklyHours.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}시간
          {result.isAllowanceEligible
            ? ` · 주휴수당 대상 (주휴시간 ${result.allowanceHours.toFixed(2)}시간)`
            : ' · 주휴수당 미대상 (주 15시간 미만)'}
          {' · '}월 총 근무시간 {formatHours(result.monthlyTotalHours)} (실근로 {formatHours(result.monthlyWorkHours)} + 주휴 {formatHours(result.monthlyAllowanceHours)})
        </p>
        </>
        )}

        {payType === 'salary' && (
          <div className="payroll-calculator__field-grid">
            <label className="payroll-calculator__field">
              <span>직원 선택</span>
              <select
                value={selectedStaffId ?? ''}
                onChange={(e) => setSelectedStaffId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">직접 입력</option>
                {payStaffList.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                    {member.role === 'admin' ? ' (대표)' : ''}
                    {member.department || member.position
                      ? ` · ${[member.department, member.position].filter(Boolean).join(' ')}`
                      : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="payroll-calculator__field">
              <span>월 급여 (원)</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="예: 3,000,000"
                value={formatNumberInput(monthlySalary)}
                onChange={(e) => setMonthlySalary(parseNumberInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>추가근무 (일)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={overtimeDaysInput}
                onChange={(e) => setOvertimeDaysInput(sanitizeDecimalInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>추가근무 (시간)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={overtimeHoursInput}
                onChange={(e) => setOvertimeHoursInput(sanitizeDecimalInput(e.target.value))}
              />
            </label>
          </div>
        )}
        {payType === 'salary' && result.overtimeTotalHours > 0 && (
          <p className="payroll-calculator__note">
            추가근무 총 {formatHours(result.overtimeTotalHours)} (1일 = {OVERTIME_HOURS_PER_DAY}시간)
            {' · '}통상시급 {numberKRW(Math.round(result.ordinaryHourlyWage))} (월 급여 ÷ {SALARY_STANDARD_MONTHLY_HOURS}시간)
            {' · '}가산율 {OVERTIME_MULTIPLIER}배
          </p>
        )}
      </div>

      <div className="payroll-calculator__card">
        <h3 className="payroll-calculator__card-title">공제 설정</h3>
        {payType === 'hourly' && (
          <div className="payroll-calculator__tabs">
            <button
              type="button"
              className={`payroll-calculator__tab${deductionMode === 'insurance' ? ' is-active' : ''}`}
              onClick={() => setDeductionMode('insurance')}
            >
              4대보험 (근로소득)
            </button>
            <button
              type="button"
              className={`payroll-calculator__tab${deductionMode === 'freelance' ? ' is-active' : ''}`}
              onClick={() => setDeductionMode('freelance')}
            >
              사업소득세 3.3% (프리랜서·일용직)
            </button>
            <button
              type="button"
              className={`payroll-calculator__tab${deductionMode === 'none' ? ' is-active' : ''}`}
              onClick={() => setDeductionMode('none')}
            >
              공제 없음
            </button>
          </div>
        )}

        {deductionMode === 'insurance' && (
          <div className="payroll-calculator__field-grid">
            <label className="payroll-calculator__field">
              <span>국민연금 (%)</span>
              <input
                type="text"
                inputMode="decimal"
                value={pensionRate}
                onChange={(e) => setPensionRate(parseRateInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>건강보험 (%)</span>
              <input
                type="text"
                inputMode="decimal"
                value={healthRate}
                onChange={(e) => setHealthRate(parseRateInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>장기요양보험 (건강보험료의 %)</span>
              <input
                type="text"
                inputMode="decimal"
                value={longTermCareRate}
                onChange={(e) => setLongTermCareRate(parseRateInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>고용보험 (%)</span>
              <input
                type="text"
                inputMode="decimal"
                value={employmentRate}
                onChange={(e) => setEmploymentRate(parseRateInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>소득세 + 지방소득세 (원, 직접입력)</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="간이세액표 기준 금액"
                value={formatNumberInput(incomeTax)}
                onChange={(e) => setIncomeTax(parseNumberInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>기타 공제 (원)</span>
              <input
                type="text"
                inputMode="numeric"
                value={formatNumberInput(etcDeduction)}
                onChange={(e) => setEtcDeduction(parseNumberInput(e.target.value))}
              />
            </label>
          </div>
        )}

        {deductionMode === 'freelance' && (
          <div className="payroll-calculator__field-grid">
            <label className="payroll-calculator__field">
              <span>사업소득세율 (%)</span>
              <input
                type="text"
                inputMode="decimal"
                value={freelanceRate}
                onChange={(e) => setFreelanceRate(parseRateInput(e.target.value))}
              />
            </label>
            <label className="payroll-calculator__field">
              <span>기타 공제 (원)</span>
              <input
                type="text"
                inputMode="numeric"
                value={formatNumberInput(etcDeduction)}
                onChange={(e) => setEtcDeduction(parseNumberInput(e.target.value))}
              />
            </label>
          </div>
        )}

        {deductionMode !== 'none' && (
          <button
            type="button"
            className="payroll-calculator__default-rates-btn"
            onClick={() => {
              setPensionRate(DEFAULT_RATES.pension);
              setHealthRate(DEFAULT_RATES.health);
              setLongTermCareRate(DEFAULT_RATES.longTermCare);
              setEmploymentRate(DEFAULT_RATES.employment);
              setFreelanceRate(DEFAULT_RATES.freelance);
            }}
          >
            기본값 적용
          </button>
        )}
      </div>

      <div className="payroll-calculator__card payroll-calculator__result">
        <h3 className="payroll-calculator__card-title">계산 결과</h3>
        {payType === 'hourly' && (
          <>
            <div className="payroll-calculator__result-row payroll-calculator__result-row--hours">
              <span>총 근무시간 (월)</span>
              <strong>{formatHours(result.monthlyTotalHours)}</strong>
            </div>
            <div className="payroll-calculator__result-row">
              <span>기본급 (월)</span>
              <strong>{numberKRW(result.basePayMonthly)}</strong>
            </div>
            <div className="payroll-calculator__result-row">
              <span>주휴수당 (월)</span>
              <strong>{numberKRW(result.allowancePayMonthly)}</strong>
            </div>
          </>
        )}
        {payType === 'salary' && (
          <>
            <div className="payroll-calculator__result-row">
              <span>월 급여</span>
              <strong>{numberKRW(result.basePayMonthly)}</strong>
            </div>
            <div className="payroll-calculator__result-row">
              <span>추가근무수당</span>
              <strong>{numberKRW(result.overtimePay)}</strong>
            </div>
          </>
        )}
        <div className="payroll-calculator__result-row payroll-calculator__result-row--sub">
          <span>세전 월급 (합계)</span>
          <strong>{numberKRW(result.grossPay)}</strong>
        </div>

        {deductionMode !== 'none' && (
          <>
            {deductionMode === 'insurance' && (
              <>
                <div className="payroll-calculator__result-row payroll-calculator__result-row--deduction">
                  <span>국민연금</span>
                  <strong>-{numberKRW(result.pensionAmount)}</strong>
                </div>
                <div className="payroll-calculator__result-row payroll-calculator__result-row--deduction">
                  <span>건강보험</span>
                  <strong>-{numberKRW(result.healthAmount)}</strong>
                </div>
                <div className="payroll-calculator__result-row payroll-calculator__result-row--deduction">
                  <span>장기요양보험</span>
                  <strong>-{numberKRW(result.longTermCareAmount)}</strong>
                </div>
                <div className="payroll-calculator__result-row payroll-calculator__result-row--deduction">
                  <span>고용보험</span>
                  <strong>-{numberKRW(result.employmentAmount)}</strong>
                </div>
              </>
            )}
            {deductionMode === 'freelance' && (
              <div className="payroll-calculator__result-row payroll-calculator__result-row--deduction">
                <span>사업소득세</span>
                <strong>-{numberKRW(result.freelanceAmount)}</strong>
              </div>
            )}
            <div className="payroll-calculator__result-row payroll-calculator__result-row--deduction">
              <span>{deductionMode === 'insurance' ? '소득세 + 기타 공제' : '기타 공제'}</span>
              <strong>-{numberKRW(result.manualDeductions)}</strong>
            </div>
            <div className="payroll-calculator__result-row payroll-calculator__result-row--sub">
              <span>총 공제액</span>
              <strong>-{numberKRW(result.totalDeductions)}</strong>
            </div>
          </>
        )}

        <div className="payroll-calculator__result-row payroll-calculator__result-row--final">
          <span>{deductionMode === 'none' ? '월급' : '실수령액'}</span>
          <strong>{numberKRW(result.netPay)}</strong>
        </div>
      </div>
      </div>

      <div className="payroll-calculator__right">
        <div className="payroll-calculator__card payroll-calculator__payslip" ref={payslipRef}>
          <div className="payroll-calculator__payslip-header">
            <span className="payroll-calculator__payslip-company">예플랫</span>
            <h3 className="payroll-calculator__payslip-title">{viewYear}년 {viewMonth}월 급여명세서</h3>
          </div>

          <div className="payroll-calculator__payslip-info">
            <label className="payroll-calculator__payslip-info-row">
              <span>성명</span>
              <input
                type="text"
                className="payroll-calculator__payslip-input"
                placeholder="직원 이름"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
              />
            </label>
            <div className="payroll-calculator__payslip-info-row">
              <span>소속</span>
              <strong>
                {[selectedStaff?.department, selectedStaff?.position].filter(Boolean).join(' · ') || '-'}
              </strong>
            </div>
            <div className="payroll-calculator__payslip-info-row">
              <span>지급 대상기간</span>
              <strong>{viewYear}년 {viewMonth}월</strong>
            </div>
            {payType === 'hourly' && (
              <>
                <div className="payroll-calculator__payslip-info-row">
                  <span>순 근무시간</span>
                  <strong>{formatHours(result.monthlyWorkHours)}</strong>
                </div>
                <div className="payroll-calculator__payslip-info-row">
                  <span>주휴시간</span>
                  <strong>{formatHours(result.monthlyAllowanceHours)}</strong>
                </div>
                <div className="payroll-calculator__payslip-info-row">
                  <span>총 근무시간</span>
                  <strong>{formatHours(result.monthlyTotalHours)}</strong>
                </div>
              </>
            )}
          </div>

          <div className="payroll-calculator__payslip-table">
            <div className="payroll-calculator__payslip-table-head">
              <span>지급 항목</span>
              <span>금액</span>
            </div>
            {payType === 'hourly' ? (
              <>
                <div className="payroll-calculator__payslip-table-row">
                  <span>기본급</span>
                  <span>{numberKRW(result.basePayMonthly)}</span>
                </div>
                <div className="payroll-calculator__payslip-table-row">
                  <span>주휴수당</span>
                  <span>{numberKRW(result.allowancePayMonthly)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="payroll-calculator__payslip-table-row">
                  <span>월 급여</span>
                  <span>{numberKRW(result.basePayMonthly)}</span>
                </div>
                {result.overtimePay > 0 && (
                  <div className="payroll-calculator__payslip-table-row">
                    <span>추가근무수당 ({formatHours(result.overtimeTotalHours)})</span>
                    <span>{numberKRW(result.overtimePay)}</span>
                  </div>
                )}
              </>
            )}
            <div className="payroll-calculator__payslip-table-row is-total">
              <span>지급 합계</span>
              <span>{numberKRW(result.grossPay)}</span>
            </div>
          </div>

          <div className="payroll-calculator__payslip-table">
            <div className="payroll-calculator__payslip-table-head">
              <span>공제 항목</span>
              <span>금액</span>
            </div>
            {deductionMode === 'none' && (
              <div className="payroll-calculator__payslip-table-row payroll-calculator__payslip-empty">
                <span>공제 항목 없음</span>
              </div>
            )}
            {deductionMode === 'insurance' && (
              <>
                <div className="payroll-calculator__payslip-table-row">
                  <span>국민연금 ({pensionRate}%)</span>
                  <span>-{numberKRW(result.pensionAmount)}</span>
                </div>
                <div className="payroll-calculator__payslip-table-row">
                  <span>건강보험 ({healthRate}%)</span>
                  <span>-{numberKRW(result.healthAmount)}</span>
                </div>
                <div className="payroll-calculator__payslip-table-row">
                  <span>장기요양보험 ({longTermCareRate}%)</span>
                  <span>-{numberKRW(result.longTermCareAmount)}</span>
                </div>
                <div className="payroll-calculator__payslip-table-row">
                  <span>고용보험 ({employmentRate}%)</span>
                  <span>-{numberKRW(result.employmentAmount)}</span>
                </div>
                <div className="payroll-calculator__payslip-table-row">
                  <span>소득세 + 지방소득세</span>
                  <span>-{numberKRW(incomeTax)}</span>
                </div>
                {etcDeduction > 0 && (
                  <div className="payroll-calculator__payslip-table-row">
                    <span>기타 공제</span>
                    <span>-{numberKRW(etcDeduction)}</span>
                  </div>
                )}
              </>
            )}
            {deductionMode === 'freelance' && (
              <>
                <div className="payroll-calculator__payslip-table-row">
                  <span>사업소득세 ({freelanceRate}%)</span>
                  <span>-{numberKRW(result.freelanceAmount)}</span>
                </div>
                {etcDeduction > 0 && (
                  <div className="payroll-calculator__payslip-table-row">
                    <span>기타 공제</span>
                    <span>-{numberKRW(etcDeduction)}</span>
                  </div>
                )}
              </>
            )}
            {deductionMode !== 'none' && (
              <div className="payroll-calculator__payslip-table-row is-total">
                <span>공제 합계</span>
                <span>-{numberKRW(result.totalDeductions)}</span>
              </div>
            )}
          </div>

          <div className="payroll-calculator__payslip-net">
            <span>실수령액</span>
            <strong>{numberKRW(result.netPay)}</strong>
          </div>

          <p className="payroll-calculator__payslip-footer">
            본 명세서는 예상 금액이며, 실제 지급액과 다를 수 있습니다.
          </p>
        </div>

        <button
          type="button"
          className="payroll-calculator__screenshot-btn"
          disabled={capturingPayslip}
          onClick={() => void handleCapturePayslip()}
        >
          {capturingPayslip ? '저장 중…' : '스크린샷 저장'}
        </button>
      </div>
      </div>

      <div className="payroll-calculator__save-bar">
        {settingsMessage && (
          <p
            className={`payroll-calculator__save-message payroll-calculator__save-message--${settingsMessage.type}`}
          >
            {settingsMessage.text}
          </p>
        )}
        {onShowRecordsList && (
          <button
            type="button"
            className="payroll-calculator__history-btn"
            onClick={onShowRecordsList}
          >
            지난급여목록
          </button>
        )}
        <button
          type="button"
          className="payroll-calculator__save-btn"
          disabled={savingSettings}
          onClick={() => void handleSave()}
        >
          {savingSettings ? '저장 중…' : '저장하기'}
        </button>
      </div>
    </div>
  );
}
